import { apiFetch } from './http';

/**
 * A night's setting beyond the server's own eras (`api/servers.ts`): who the
 * platform proposes to fight it, from the era and from where the terrain is
 * (docs/design/SERVER_SETTING_STUDY.md).
 */

/** A faction the mod set has: its class, the name a person reads, and the side it fights on. */
export interface FactionRef {
  className: string;
  displayName: string;
  side: string;
}

/**
 * The factions proposed for a night, most fitting first. Empty lists are
 * "nothing to propose", never "nothing allowed". `theatreMatters` is true in the
 * eras in which the place decides who fights (the Second World War); `theatre`
 * is then where the terrain is, or null when nobody has said and the name gives
 * nothing away — and the quick start asks.
 */
export interface SettingDefaults {
  era: string;
  theatre: string | null;
  theatreMatters: boolean;
  ours: FactionRef[];
  enemy: FactionRef[];
  civilians: FactionRef[];
}

/**
 * A theatre of an era (`era/theatres.json` on the platform): `label` is what a
 * select or a sentence reads, `chip` the shorter name a filter chip shows.
 */
export interface Theatre {
  id: string;
  label: string;
  chip: string;
  words: string[];
}

/** An era a night can be set in, with its theatres in the order a select lists them. */
export interface EraEntry {
  id: string;
  label: string;
  theatres: Theatre[];
}

/**
 * The eras and theatres the platform knows, from its own table, so a theatre
 * added there shows here with no change to the web
 * (docs/design/COMPOSITION_LIBRARY.md §2.4).
 */
export const fetchEras = () => apiFetch<EraEntry[]>('/api/setting/eras');

export const fetchSettingDefaults = (serverId: number, world: string, era?: string | null) => {
  const params = new URLSearchParams({ serverId: String(serverId), world });
  if (era) params.set('era', era);
  return apiFetch<SettingDefaults>(`/api/setting/defaults?${params.toString()}`);
};

/** Remembers where a terrain is, for everybody and for every later night; an empty theatre forgets it. */
export const saveTerrainTheatre = (world: string, theatre: string) =>
  apiFetch<{ world: string; theatre: string }>(
    `/api/setting/terrains/${encodeURIComponent(world)}`,
    { method: 'PUT', body: { theatre } },
  );
